/**
 * Pure helpers that filter the explorer tree by a search term.
 *
 * Extracted from `DatabaseExplorer.tsx` so the filter logic is unit-testable.
 *
 * **Pinned wrappers are excluded from search.** Each `pinned-*` wrapper is a
 * copy of an object that ALSO lives under `Databases → …` in the same tree —
 * including both in the searchable set caused every search hit on a pinned
 * object to surface twice (once via the pinned shortcut, once via the
 * canonical row), confusing users. The user-approved policy is: the pinned
 * region is the entry point when search is inactive; when the user types a
 * query, only the canonical `Databases → …` subtree is searched.
 *
 * Spec: Docs/superpowers/specs/2026-05-26-object-explorer-pinning-design.md
 *       §"Object Explorer search"
 */

import type { TreeNode } from '../../explorer/models/TreeModel';

/**
 * Node types whose `name` participates in tree-search matching. Pinned
 * wrappers (`pinned-database`, `pinned-schema`, `pinned-folder`,
 * `pinned-object`) are deliberately EXCLUDED — see the file-level doc
 * comment above for the rationale.
 */
export const SEARCHABLE_NODE_TYPES = new Set<string>([
  'connection',
  'database',
  'table',
  'view',
  'stored-procedure',
  'function',
  'package',
  // SQL Server user-defined types (1.4.21) — the report that started this
  // was literally "I couldn't find these", so search must see them.
  'table-type',
  'alias-type',
  'clr-type',
  // Azure Storage resources (spec §5.1) — the service folders are not searchable.
  'storage-container',
  'storage-table',
  'storage-queue',
  'storage-share',
]);

export function filterTreeNodes(nodes: TreeNode[], term: string): TreeNode[] {
  if (!term.trim()) return nodes;

  const lowerTerm = term.toLowerCase();

  // ancestorMatchedAndExpanded: true if an ancestor matched the search AND was
  // expanded (lets the user drill down into a matched subtree without the
  // unrelated siblings disappearing from view).
  const filterNode = (node: TreeNode, ancestorMatchedAndExpanded: boolean): TreeNode | null => {
    const isSearchable = SEARCHABLE_NODE_TYPES.has(node.type);
    const nameMatches = isSearchable && node.name.toLowerCase().includes(lowerTerm);

    if (ancestorMatchedAndExpanded) {
      return {
        ...node,
        children: node.isExpanded && node.children
          ? node.children.map((child) => filterNode(child, true)).filter((c): c is TreeNode => c !== null)
          : node.children,
      };
    }

    const thisNodeMatchedAndExpanded = nameMatches && (node.isExpanded ?? false);
    const filteredChildren = node.children
      ?.map((child) => filterNode(child, thisNodeMatchedAndExpanded))
      .filter((child): child is TreeNode => child !== null);

    if (nameMatches || (filteredChildren && filteredChildren.length > 0)) {
      return {
        ...node,
        children: filteredChildren,
        isExpanded: filteredChildren && filteredChildren.length > 0 ? true : node.isExpanded,
      };
    }

    return null;
  };

  return nodes
    .map((node) => filterNode(node, false))
    .filter((node): node is TreeNode => node !== null);
}
